import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Mail, ArrowRight } from "lucide-react";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            toast.success("If that email exists, we've sent a reset code.");
            // Move on to the reset-verify step; pass the email as query so the
            // next screen knows which account we're resetting.
            setTimeout(() => navigate(`/reset-password?email=${encodeURIComponent(email)}`), 400);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send reset code");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-6 py-2" data-testid="forgot-password-page">
            <div className="rounded-2xl border border-border bg-card p-8">
                <div className="h-12 w-12 rounded-full bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/40 flex items-center justify-center mb-5">
                    <Mail className="h-5 w-5 text-[hsl(var(--accent))]" />
                </div>
                <h1 className="font-display text-4xl">Forgot password?</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Enter the email on your X-Plosiv account. We'll send you a 6-digit code to reset your password.
                </p>

                <form onSubmit={submit} className="space-y-4 mt-7">
                    <div>
                        <Label className="font-mono-ui mb-2 block">Email</Label>
                        <Input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded-2xl bg-background/60 border-border h-11"
                            data-testid="forgot-email-input"
                            placeholder="you@example.com"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full dw-btn-primary h-12 font-mono-ui flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="forgot-submit-button"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                        {loading ? "Sending…" : "Send reset code"}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                    Remember it?{" "}
                    <Link to="/login" className="text-foreground underline underline-offset-4" data-testid="forgot-back-to-login">
                        Back to sign in
                    </Link>
                </p>
            </div>
            <div className="h-24" />
        </div>
    );
}
